/* SUMMARY
  * Imports
  * Styles
  * Table: table
*/

/* Imports */
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { Card, CardBody, Typography } from "@material-tailwind/react";
import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
/***/

/* Styles */
import "./Table.scss";
/***/

// Types pour une meilleure séparation des responsabilités
interface TableHead {
  label: string;
  orderKey: string | null;
  width?: string;
  headClassName?: string;
}

interface TableProps {
  tableHead: TableHead[];
  children: React.ReactNode;
  emptyTable?: string | boolean;
  selectedHead?: string;
  orderDesc?: boolean;
  onHeadClick?: (head: string, orderDesc: boolean) => void;
  className?: string;
  enableRowLinks?: boolean;
}

// Hook personnalisé pour la gestion des liens
const useLinkEnrichment = (enableRowLinks: boolean, navigate: ReturnType<typeof useNavigate> | null) => {
  const enrichChildren = useCallback((children: React.ReactNode): React.ReactNode => {
    if (!enableRowLinks || !children) return children;
  
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
  
      const isTableRowLike =
        (typeof child.type === "string" && child.type === "tr") ||
        typeof child.type === "function";
  
      if (!isTableRowLike) return child;
  
      // Si c'est un composant personnalisé (comme WorkorderListItem), on le laisse tel quel
      // car il gère déjà ses propres événements
      if (typeof child.type === "function") {
        return child;
      }
  
      const props = (child.props || {}) as any;
      const { onClick, onMouseDown, "data-href": href, children: rowChildren, ...otherProps } = props;
  
      if (!href) return child;
  
      const enhancedOnClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          window.open(href, "_blank");
          return;
        }
        if (onClick) onClick(e);
      };
  
      const enhancedOnMouseDown = (e: React.MouseEvent<HTMLTableRowElement>) => {
        if (e.button === 1) {
          e.preventDefault();
          window.open(href, "_blank");
          return;
        }
        if (onMouseDown) onMouseDown(e);
      };
  
      const newRowChildren = React.Children.map(rowChildren, (cell) => {
        if (!React.isValidElement(cell) || cell.type !== "td") return cell;
  
        const cellProps = (cell.props || {}) as any;
        if (cellProps["data-no-link"]) return cell;
  
        return React.cloneElement(cell, {
          ...cellProps,
          children: (
            <a
              href={href}
              className="block w-full h-full"
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey) {
                  e.stopPropagation();
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
                if (navigate) navigate(href);
              }}
              onMouseDown={(e) => {
                if (e.button === 1) e.stopPropagation();
              }}
            >
              {cellProps.children}
            </a>
          )
        });
      });
  
      return React.cloneElement(child, {
        ...otherProps,
        onClick: enhancedOnClick,
        onMouseDown: enhancedOnMouseDown,
        children: newRowChildren
      });
    });
  }, [enableRowLinks, navigate]);

  return { enrichChildren };
};

// Hook personnalisé pour la gestion du scroll
const useScrollShadows = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftShadow(scrollLeft > 5);
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 5);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    handleScroll();
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return { scrollContainerRef, showLeftShadow, showRightShadow };
};

/**
* Table: table
* Used in views that contain a table
* Display a table elem with its head and 
* allow to sort by column
* @param tableHead - table head, columns names
* @param children - table content
* @param emptyTable - Text display on empty table
* @param orderDesc - sort order
* @param onHeadClick - column name click callback
* @param className - additional classNames
* @param enableRowLinks - whether to enable proper link behavior for rows
*/
export function Table({
  tableHead,
  children,
  emptyTable,
  selectedHead,
  orderDesc,
  onHeadClick,
  className = "",
  enableRowLinks = false
}: TableProps) {
  const navigate = useNavigate();
  const { enrichChildren } = useLinkEnrichment(enableRowLinks, navigate);
  const { scrollContainerRef, showLeftShadow, showRightShadow } = useScrollShadows();

  const onHeadSelected = useCallback((head: string) => {
    const isDesc = head !== selectedHead ? false : !orderDesc;
    if (onHeadClick) {
      onHeadClick(head, isDesc);
    }
  }, [selectedHead, orderDesc, onHeadClick]);

  const filteredHead = tableHead.filter((head) => head && head.label);

  return (
    <Card className={`et-table ${className}`}>
      <CardBody className="p-0">
        <div className="table-container" ref={scrollContainerRef}>
          <table>
            <colgroup>
              {filteredHead.map((head, index) => (
                <col key={`col-${head.label}-${index}`} style={{ width: head.width || "auto" }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {filteredHead.map((head, index) => {
                  const isLast = index === filteredHead.length - 1;
                  const lastClass = isLast ? (head.width ? "" : "w-[78px]") : "";
                  return (
                    <th
                      key={`${head.label}-${index}`}
                      onClick={() => { if (head.orderKey) onHeadSelected(head.orderKey); }}
                      className={`et-table-head ${lastClass} ${head.orderKey === null ? "no-order" : ""} ${head.headClassName || ""}`}
                      style={{ width: head.width || "auto" }}
                    >
                      <div className="head">
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal opacity-70 whitespace-normal leading-tight"
                        >
                          {String(head?.label || "").toUpperCase()}
                        </Typography>

                        {selectedHead === head.orderKey && (
                          orderDesc ? (
                            <ChevronDownIcon className="w-[16px]" />
                          ) : (
                            <ChevronUpIcon className="w-[16px]" />
                          )
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>{enrichChildren(children)}</tbody>
          </table>
        </div>

        <div className={`scroll-shadow-left ${showLeftShadow ? "visible" : ""}`} />
        <div className={`scroll-shadow-right ${showRightShadow ? "visible" : ""}`} />

        {(emptyTable && emptyTable !== "" && React.Children.count(children) === 0) && (
          <Typography className="py-1 text-center">
            {typeof emptyTable === "string" ? emptyTable : ""}
          </Typography>
        )}
      </CardBody>
    </Card>
  );
}
